import { useState, useEffect, useRef } from "react"
import NotesIcon from "@assets/icons/notes"
import { PenIcon } from "@assets/icons"
import formatDateFromISOString from "src/utils/formatDate"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import EditIcon from "@mui/icons-material/Edit"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import usePatient from "@hooks/usePatient"
import { useSelector } from "react-redux"

// Visual acuity suggestion values
const visualAcuitySuggestions = [
  "6/6 (20/20)",
  "6/9 (20/30)",
  "6/12 (20/40)",
  "6/18 (20/60)",
  "6/24 (20/80)",
  "6/30 (20/100)",
  "6/40 (20/200)",
  "6/60 (20/400)",
]

const MetricsTable = ({ patientData, refreshPatientData }) => {
  // Add a ref for the table container
  const metricsTableRef = useRef(null)
  const { handleAddReading } = usePatient()
  const { isAddReadingLoading } = useSelector((state) => state.patient)

  // Add pagination state for mobile view
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 3 // Show 3 items per page on mobile

  // Format today's date as YYYY-MM-DD for the date input
  const getTodayFormatted = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, "0")
    const day = String(today.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  // New state for the metrics form with today's date as default
  const [newMetric, setNewMetric] = useState({
    date: getTodayFormatted(),
    visualAcuity: "",
    contrastSensitivity: "",
    comment: "",
  })

  // Add state for suggestions
  const [currentSuggestion, setCurrentSuggestion] = useState("")
  const visualAcuityInputRef = useRef(null)

  const [isEditingMetric, setIsEditingMetric] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  // Table header cell styles for metrics table
  const tableHeadCellStyles = {
    color: "var(--bg-theme-primary)",
    fontWeight: "700",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "12px 24px 12px 24px",
    height: "42px",
    backgroundColor: "var(--bg-blue-light)",
    "@media (max-width:823px)": {
      minWidth: "150px",
    },
    minWidth: "200px",
  }

  // When clicking pencil icon, enter edit mode
  const handleEditClick = () => {
    setIsEditingMetric(true)
    // Force a re-render to ensure the TextFields become active
    setTimeout(() => {
      // Only find inputs within the metrics table component
      if (metricsTableRef.current) {
        const inputs = metricsTableRef.current.querySelectorAll(
          'input[type="text"], input[type="date"]'
        )
        if (inputs.length > 0) {
          inputs[0].focus()
        }
      }
    }, 100)
  }

  // Reset form validation when editing mode changes
  useEffect(() => {
    if (isEditingMetric) {
      // Check if form is valid when entering edit mode - now include comment field
      const isValid =
        !!newMetric.date &&
        !!newMetric.visualAcuity &&
        !!newMetric.contrastSensitivity &&
        !!newMetric.comment
      setIsFormValid(isValid)
    }
  }, [isEditingMetric, newMetric])

  // Function to find matching suggestion
  const findSuggestion = (value) => {
    if (!value) return ""

    const lowerValue = value.toLowerCase()
    return (
      visualAcuitySuggestions.find(
        (suggestion) =>
          suggestion.toLowerCase().startsWith(lowerValue) &&
          suggestion.toLowerCase() !== lowerValue
      ) || ""
    )
  }

  // Handle metrics input change with suggestion support
  const handleMetricChange = (field, value) => {
    const updatedMetric = { ...newMetric, [field]: value }
    setNewMetric(updatedMetric)

    // Update suggestion for visual acuity field
    if (field === "visualAcuity") {
      setCurrentSuggestion(findSuggestion(value))
    }

    // Check if form is valid (all required fields filled) - now include comment field
    const isValid =
      !!updatedMetric.date &&
      !!updatedMetric.visualAcuity &&
      !!updatedMetric.contrastSensitivity &&
      !!updatedMetric.comment
    setIsFormValid(isValid)
  }

  // Handle tab key to accept suggestion
  const handleKeyDown = (e) => {
    if (e.key === "Tab" && currentSuggestion) {
      e.preventDefault()
      setNewMetric({
        ...newMetric,
        visualAcuity: currentSuggestion,
      })
      setCurrentSuggestion("")

      // Move focus to the next field
      const inputs = metricsTableRef.current.querySelectorAll(
        'input[type="text"], textarea'
      )
      const currentIndex = Array.from(inputs).indexOf(e.target)
      if (currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus()
      }
    }
  }

  // Handle metrics submission with API call
  const handleSubmitMetric = () => {
    if (!isFormValid) {
      // Don't submit if form is invalid
      return
    }

    // Prepare data for the API call
    const readingData = {
      patientId: patientData._id,
      visualAcuity: newMetric.visualAcuity,
      contrastSensitivity: newMetric.contrastSensitivity,
      comment: newMetric.comment,
      date: new Date().toISOString(),
    }

    // Call the API using the handler from usePatient hook
    handleAddReading(readingData, onAddReadingSuccess)
  }

  // Success callback after adding a reading
  const onAddReadingSuccess = () => {
    setNewMetric({
      date: getTodayFormatted(),
      visualAcuity: "",
      contrastSensitivity: "",
      comment: "",
    })
    setIsEditingMetric(false)
    setIsFormValid(false)

    // Refresh patient data to show the new reading
    if (refreshPatientData) {
      refreshPatientData()
    }
  }

  // Calculate paginated data for mobile view
  const getPaginatedReadings = () => {
    if (!patientData?.readingHistory) return []

    const readings = [...patientData.readingHistory].reverse()
    const totalPages = Math.ceil(readings.length / itemsPerPage)

    // Ensure current page is valid
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }

    const startIndex = (currentPage - 1) * itemsPerPage
    return readings.slice(startIndex, startIndex + itemsPerPage)
  }

  const totalPages = patientData?.readingHistory
    ? Math.ceil(patientData.readingHistory.length / itemsPerPage)
    : 0

  // Handle pagination navigation
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  // Add state for tracking viewport width
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  )

  // Set up listener for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Helper function to safely get date from reading
  const getReadingDate = (reading) => {
    if (!reading) return null
    // Use date if available, otherwise fall back to created_at or updated_at
    return reading.date || reading.created_at || reading.updated_at || null
  }

  return (
    <div
      className="py-10 px-8 rounded-2xl bg-[--primary-blue-light] flex flex-col gap-4"
      ref={metricsTableRef}
    >
      <div className="flex items-center justify-between">
        <div className="gap-2 flex items-center">
          <NotesIcon stroke="var(--primary-blue)" />
          <div className="text-xs text-[--text-light-gray] font-bold tracking-widest">
            METRICS
          </div>
        </div>
      </div>

      <TableContainer
        component={Paper}
        sx={{
          border: "1px solid #EAEAEA",
          borderRadius: "16px",
          position: "relative",
          minHeight: "300px",
          maxHeight: "500px",
          width: "100%",
          overflowX: "auto", // Changed from "visible" to "auto" for better responsiveness
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--border-theme-300)",
            borderRadius: "8px",
          },
          boxShadow: "none",
          "@media (max-width:700px)": {
            borderRadius: "16px",
            "&::-webkit-scrollbar": {
              width: "0px",
            },
          },
        }}
      >
        <Table
          stickyHeader
          aria-label="metrics table"
          sx={{
            tableLayout: "fixed",
            minWidth: "900px", // Increased minimum width
            width: "100%", // Ensure table uses all available space
            "& .MuiTableCell-root": {
              color: "#475467",
              fontFamily: "Manrope",
            },
            // Add specific styles for action column in mobile view
            "@media (max-width: 768px)": {
              "& .action-cell": {
                position: "sticky",
                right: 0,
                background: "white",
                boxShadow: "-5px 0 5px -2px rgba(0, 0, 0, 0.1)",
                zIndex: 2,
              },
            },
          }}
        >
          <TableHead
            sx={{
              backgroundColor: "var(--bg-blue-light)",
            }}
          >
            <TableRow>
              <TableCell
                sx={{ ...tableHeadCellStyles, width: "18%" }}
                align="left"
              >
                Date
              </TableCell>
              <TableCell
                sx={{ ...tableHeadCellStyles, width: "22%" }}
                align="left"
              >
                Visual Acuity
              </TableCell>
              <TableCell
                sx={{ ...tableHeadCellStyles, width: "22%" }}
                align="left"
              >
                Contrast Sensitivity
              </TableCell>
              <TableCell
                sx={{ ...tableHeadCellStyles, width: "33%" }}
                align="left"
              >
                Comment
              </TableCell>
              <TableCell
                sx={{
                  ...tableHeadCellStyles,
                  width: "5%",
                  minWidth: "60px",
                }}
                align="center"
              >
                {/* Empty header for slimmer action column */}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ position: "relative" }}>
            {/* First row with add functionality */}
            <TableRow
              sx={{
                backgroundColor: isEditingMetric ? "#f5f9ff" : "white",
                "&:hover": {
                  backgroundColor: isEditingMetric ? "#f5f9ff" : "#f8f8f8",
                },
              }}
            >
              <TableCell
                align="left"
                sx={{ padding: "16px 24px", color: "#475467" }}
              >
                {"Today"}
              </TableCell>
              <TableCell align="left" sx={{ padding: "16px 24px" }}>
                {isEditingMetric ? (
                  <div className="relative">
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Visual Acuity"
                      value={newMetric.visualAcuity}
                      onChange={(e) =>
                        handleMetricChange("visualAcuity", e.target.value)
                      }
                      onKeyDown={handleKeyDown}
                      inputRef={visualAcuityInputRef}
                      variant="standard"
                      disabled={false}
                      onClick={(e) => e.currentTarget.focus()}
                      InputProps={{
                        disableUnderline: true,
                        style: {
                          fontFamily: "Manrope",
                          fontWeight: 400,
                          fontSize: "12px",
                          lineHeight: "16px",
                          letterSpacing: "0.4px",
                          color: "#475467",
                        },
                      }}
                    />
                    {currentSuggestion && (
                      <div
                        className="absolute top-0 left-0 text-gray-400 pointer-events-none"
                        style={{
                          fontFamily: "Manrope",
                          fontWeight: 400,
                          fontSize: "12px",
                          lineHeight: "16px",
                          letterSpacing: "0.4px",
                        }}
                      >
                        {newMetric.visualAcuity}
                        <span className="text-gray-300">
                          {currentSuggestion.substring(
                            newMetric.visualAcuity.length
                          )}
                        </span>
                        <span className="ml-2 text-gray-400 text-xs italic">
                          (press Tab to complete)
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ color: "#475467" }}>Visual Acuity</div>
                )}
              </TableCell>
              <TableCell align="left" sx={{ padding: "16px 24px" }}>
                {isEditingMetric ? (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Contrast Sensitivity"
                    value={newMetric.contrastSensitivity}
                    onChange={(e) =>
                      handleMetricChange("contrastSensitivity", e.target.value)
                    }
                    variant="standard"
                    disabled={false}
                    onClick={(e) => e.currentTarget.focus()}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontFamily: "Manrope",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.4px",
                        color: "#475467",
                      },
                    }}
                  />
                ) : (
                  <div style={{ color: "#475467" }}>Contrast Sensitivity</div>
                )}
              </TableCell>
              <TableCell align="left" sx={{ padding: "16px 24px" }}>
                {isEditingMetric ? (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add comment"
                    value={newMetric.comment}
                    onChange={(e) =>
                      handleMetricChange("comment", e.target.value)
                    }
                    variant="standard"
                    multiline
                    rows={2}
                    disabled={false}
                    onClick={(e) => e.currentTarget.focus()}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontFamily: "Manrope",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.4px",
                        color: "#475467",
                      },
                    }}
                  />
                ) : (
                  <div
                    className="text-gray-400"
                    style={{
                      fontFamily: "Manrope",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "16px",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Comment
                  </div>
                )}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  padding: "8px 4px",
                  minWidth: "60px",
                  width: "5%",
                  position: "relative",
                  zIndex: 2,
                  visibility: "visible",
                  // Make sure action cell is always visible
                  "@media (max-width: 768px)": {
                    position: "sticky",
                    right: 0,
                    background: isEditingMetric ? "#f5f9ff" : "white",
                    boxShadow: "-5px 0 5px -2px rgba(0, 0, 0, 0.1)",
                  },
                }}
                className="action-cell"
              >
                {isEditingMetric ? (
                  <IconButton
                    color="primary"
                    onClick={handleSubmitMetric}
                    disabled={!isFormValid || isAddReadingLoading}
                    title="Save new entry"
                    sx={{
                      padding: "6px",
                      backgroundColor: isFormValid
                        ? "rgba(0, 123, 255, 0.1)"
                        : "rgba(200, 200, 200, 0.2)",
                      border: `2px solid ${
                        isFormValid ? "var(--primary-blue)" : "#c0c0c0"
                      }`,
                      visibility: "visible !important",
                      display: "inline-flex !important",
                      opacity: isFormValid ? 1 : 0.6,
                      cursor: isFormValid ? "pointer" : "not-allowed",
                      zIndex: 10,
                      position: "relative",
                    }}
                  >
                    <CheckCircleIcon
                      fontSize="small"
                      sx={{
                        color: isFormValid ? "var(--primary-blue)" : "#c0c0c0",
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditClick()
                    }}
                    title="Add new entry"
                    sx={{
                      padding: "6px",
                      backgroundColor: "var(--pure-white)",
                      "&:hover": {
                        backgroundColor: "var(--bg-blue-light)",
                      },
                      border: "1px solid var(--primary-blue)",
                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)",
                      display: "inline-flex",
                      visibility: "visible !important",
                      zIndex: 10,
                      position: "relative",
                      pointerEvents: "auto",
                    }}
                  >
                    <PenIcon className="text-[#005BB2]" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>

            {/* Display readings based on device size - using conditional rendering instead of nested divs */}
            {patientData?.readingHistory &&
              patientData.readingHistory.length > 0 && (
                <>
                  {/* On mobile, show paginated results */}
                  {isMobile
                    ? getPaginatedReadings().map((reading, index) => (
                        <TableRow
                          key={`mobile-${reading?._id || index}`}
                          sx={{
                            "&:hover": { backgroundColor: "#f5f9ff" },
                            "& .MuiTableCell-root": { color: "#475467" },
                          }}
                        >
                          <TableCell align="left" sx={{ padding: "16px 24px" }}>
                            {getReadingDate(reading)
                              ? formatDateFromISOString(getReadingDate(reading))
                                  .date
                              : "N/A"}
                          </TableCell>
                          <TableCell align="left" sx={{ padding: "16px 24px" }}>
                            {reading?.visualAcuity || "N/A"}
                          </TableCell>
                          <TableCell align="left" sx={{ padding: "16px 24px" }}>
                            {reading?.contrastSensitivity || "N/A"}
                          </TableCell>
                          <TableCell align="left" sx={{ padding: "16px 24px" }}>
                            {reading?.comment || "N/A"}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              padding: "8px 4px",
                              position: "sticky",
                              right: 0,
                              background: "white",
                              boxShadow: "-5px 0 5px -2px rgba(0, 0, 0, 0.1)",
                              zIndex: 2,
                            }}
                            className="action-cell"
                          >
                            {/* Empty action cell for consistent layout but styled to match */}
                            <div className="w-10 h-10 flex items-center justify-center"></div>
                          </TableCell>
                        </TableRow>
                      ))
                    : /* On desktop, show all results */
                      [...patientData.readingHistory]
                        .reverse()
                        .map((reading, index) => (
                          <TableRow
                            key={`desktop-${reading?._id || index}`}
                            sx={{
                              "&:hover": { backgroundColor: "#f5f9ff" },
                              "& .MuiTableCell-root": { color: "#475467" },
                            }}
                          >
                            <TableCell
                              align="left"
                              sx={{ padding: "16px 24px" }}
                            >
                              {getReadingDate(reading)
                                ? formatDateFromISOString(
                                    getReadingDate(reading)
                                  ).date
                                : "N/A"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ padding: "16px 24px" }}
                            >
                              {reading?.visualAcuity || "N/A"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ padding: "16px 24px" }}
                            >
                              {reading?.contrastSensitivity || "N/A"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ padding: "16px 24px" }}
                            >
                              {reading?.comment || "N/A"}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ padding: "8px 4px" }}
                            >
                              {/* Empty cell for consistent layout */}
                            </TableCell>
                          </TableRow>
                        ))}
                </>
              )}
          </TableBody>
        </Table>

        {/* Show "No reading history available" message if there's no data */}
        {!patientData?.readingHistory ||
        patientData.readingHistory.length === 0 ? (
          <div className="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center p-10">
            No reading history available
          </div>
        ) : null}
      </TableContainer>

      {/* Mobile Pagination Controls */}
      {patientData?.readingHistory &&
      patientData.readingHistory.length > itemsPerPage ? (
        <div className="flex justify-center items-center gap-4 mt-4 md:hidden">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
            className={`px-3 py-1 rounded ${
              currentPage <= 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-[--primary-blue] hover:bg-[--primary-blue-light]"
            }`}
          >
            &lt; Previous
          </button>

          <span className="font-medium">
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className={`px-3 py-1 rounded ${
              currentPage >= totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-[--primary-blue] hover:bg-[--primary-blue-light]"
            }`}
          >
            Next &gt;
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default MetricsTable
